/**
 * Processes raw messages from Firestore to optimize display
 * - Combines consecutive bot messages
 * - Handles different message sender formats (bot/assistant)
 * 
 * @param {Array} rawMessages - Messages from Firestore
 * @returns {Array} - Processed messages for display
 */
export const processMessages = (rawMessages) => {
    if (!rawMessages || !rawMessages.length) return [];
    
    // Create a new array to store processed messages
    const processedMessages = [];
    let lastSender = null;
    
    // Process each message sequentially
    for (let i = 0; i < rawMessages.length; i++) {
      const message = rawMessages[i];
      
      // Normalize sender (some messages might use 'assistant' instead of 'bot')
      const normalizedSender = message.sender === 'assistant' ? 'bot' : message.sender;
      
      // If this is a user message or the first message
      if (normalizedSender === 'user') {
        // Add the message directly
        processedMessages.push({...message, sender: normalizedSender});
        lastSender = 'user';
      } 
      // If this is a bot message
      else if (normalizedSender === 'bot') {
        // If the previous message was from the bot, combine them
        const prevMessage = processedMessages[processedMessages.length - 1];
        
        if (lastSender === 'bot' && prevMessage) {
          // Combine this message with the previous bot message
          prevMessage.text += '\n\n' + message.text;
          // Take the latest timestamp
          prevMessage.timestamp = message.timestamp;
        } else {
          // This is the first bot message after a user message
          processedMessages.push({...message, sender: normalizedSender});
          lastSender = 'bot';
        }
      }
    }
  
    return processedMessages;
  };
  
  /**
   * Filters conversations based on a search term
   * 
   * @param {Array} conversations - List of conversations
   * @param {string} searchTerm - Term to search for
   * @returns {Array} - Filtered conversations
   */
  export const filterConversations = (conversations, searchTerm) => {
    if (!searchTerm) return conversations;
    
    const term = searchTerm.toLowerCase();
    
    return conversations.filter(conv => 
      conv.metadata?.title?.toLowerCase().includes(term) ||
      conv.lastMessage?.toLowerCase().includes(term)
    );
  };
  
  /**
   * Sorts conversations based on selected criteria
   * 
   * @param {Array} conversations - List of conversations
   * @param {string} sortBy - Sort criteria ('newest', 'oldest', 'alphabetical')
   * @returns {Array} - Sorted conversations
   */
  export const sortConversations = (conversations, sortBy) => {
    return [...conversations].sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return (b.metadata?.updatedAt?.seconds || 0) - (a.metadata?.updatedAt?.seconds || 0);
        case 'oldest':
          return (a.metadata?.updatedAt?.seconds || 0) - (b.metadata?.updatedAt?.seconds || 0);
        case 'alphabetical':
          return (a.metadata?.title || '').localeCompare(b.metadata?.title || '');
        default:
          return 0;
      }
    });
  };